import type { CSSProperties, FC, ReactNode, Ref, RefObject } from 'react'
import { FloatingArrow, type FloatingContext } from '@floating-ui/react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha, keyframes, lighten, ThemeProvider } from '@mui/material/styles'
import useTranslate from '@/hooks/use-translate'
import { cyan } from '@/theme/colors'
import {
  NABU_TOOL_BLUE,
  useDarkHeaderTheme,
} from '@/pages/documents/declaration/step/declarationWindowIdentity'
import { ProductTourIcon } from './ProductTourIcon'

/*
 * Bulle « coach » : bleu des outils (élément actif de la sidenav) avec une lueur, actions au cyan de la
 * marque, coins plus arrondis que les modales pour marquer la bulle du tour.
 */
const WHITE = '#FFFFFF'
const CARD_BG = NABU_TOOL_BLUE
const CARD_GLOW = alpha(lighten(NABU_TOOL_BLUE, 0.5), 0.4)
const CARD_BORDER = alpha(WHITE, 0.16)
const CARD_RADIUS = '16px'
const CARD_WIDTH_PX = { intro: 340, step: 300 }

/* Opacité seule : la position (transform) appartient à floating-ui ou au centrage. */
const cardIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

/** Contour de l'élément mis en avant : bleu éclairci, lisible sur le voile sombre. */
export const TOUR_OUTLINE = lighten(NABU_TOOL_BLUE, 0.35)

const actionSx = {
  bgcolor: cyan.main,
  color: WHITE,
  borderColor: cyan.main,
  '&:hover': { bgcolor: cyan.dark, borderColor: cyan.dark },
}

export type TourCardFloating = {
  setRef: Ref<HTMLDivElement>
  style: CSSProperties
  context: FloatingContext
  arrowRef: RefObject<SVGSVGElement>
}

export type ProductTourCardProps = {
  /** Étape centrée, sans cible : la carte d'accueil du tour. */
  intro?: boolean
  title: string
  body: string
  stepIndex: number
  total: number
  hasPrevious: boolean
  hasNext: boolean
  onPrevious: () => void
  onNext: () => void
  onDismiss: () => void
  /** Pastilles cliquables : aller à l'étape de cette position. */
  onSelectStep?: (position: number) => void
  titleId: string
  primaryButtonRef: Ref<HTMLButtonElement>
  /** Absent pour une carte centrée. */
  floating?: TourCardFloating
  zIndex: number
}

export const ProductTourCard: FC<ProductTourCardProps> = ({
  intro = false,
  title,
  body,
  stepIndex,
  total,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  onDismiss,
  onSelectStep,
  titleId,
  primaryButtonRef,
  floating,
  zIndex,
}) => {
  const { t } = useTranslate('productTour')
  const darkTheme = useDarkHeaderTheme()

  return (
    <ThemeProvider theme={darkTheme}>
      <Paper
        ref={floating?.setRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        elevation={16}
        data-testid="product-tour-bubble"
        data-product-tour
        style={floating ? { ...floating.style, zIndex: zIndex + 1 } : undefined}
        sx={{
          position: 'relative',
          width: CARD_WIDTH_PX[intro ? 'intro' : 'step'],
          maxWidth: 'calc(100vw - 32px)',
          p: intro ? 2.5 : 2,
          borderRadius: CARD_RADIUS,
          border: 1,
          borderColor: CARD_BORDER,
          bgcolor: CARD_BG,
          /* Lueur dans le coin, bords à la couleur du fond pour que la flèche s'y raccorde. */
          backgroundImage: `radial-gradient(120% 140% at 100% 0%, ${CARD_GLOW} 0%, transparent 55%)`,
          color: WHITE,
          animation: `${cardIn} 220ms ease-out`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          ...(!floating && {
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: zIndex + 1,
          }),
        }}
      >
        {floating ? (
          <FloatingArrow
            ref={floating.arrowRef}
            context={floating.context}
            width={16}
            height={8}
            tipRadius={2}
            fill={CARD_BG}
            stroke={CARD_BORDER}
            strokeWidth={1}
          />
        ) : null}
        <Stack
          direction="row"
          alignItems="center"
          spacing={intro ? 1.5 : 1}
          sx={{ mb: intro ? 2 : 1.25 }}
        >
          <CompassBadge size={intro ? 40 : 26} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: alpha(WHITE, 0.72),
                fontWeight: 600,
              }}
            >
              {t('coachLabel')}
            </Typography>
            {intro ? (
              <Typography variant="caption" sx={{ color: alpha(WHITE, 0.6) }}>
                {t('introStepsCount', { total })}
              </Typography>
            ) : null}
          </Box>
          <IconButton
            size="small"
            aria-label={t('close')}
            onClick={onDismiss}
            sx={{
              color: alpha(WHITE, 0.72),
              mr: -0.75,
              alignSelf: 'flex-start',
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
        <Typography
          id={titleId}
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 0.5, fontSize: intro ? 18 : undefined }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: alpha(WHITE, 0.8), whiteSpace: 'pre-line' }}
        >
          {body}
        </Typography>
        {/* Pastilles sur leur propre ligne : avec beaucoup d'étapes, elles laissent la place aux boutons. */}
        {intro ? null : (
          <ProgressDots
            stepIndex={stepIndex}
            total={total}
            onSelect={onSelectStep}
          />
        )}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={1}
          sx={{ mt: intro ? 2 : 1.25 }}
        >
          {intro ? (
            <Button
              size="small"
              onClick={onDismiss}
              sx={{ color: alpha(WHITE, 0.8) }}
            >
              {t('skip')}
            </Button>
          ) : null}
          {hasPrevious ? (
            <RoundButton label={t('previous')} onClick={onPrevious}>
              <ArrowBackIcon sx={{ fontSize: 16 }} />
            </RoundButton>
          ) : null}
          {hasNext && !intro ? (
            <RoundButton
              label={t('next')}
              onClick={onNext}
              filled
              buttonRef={primaryButtonRef}
            >
              <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </RoundButton>
          ) : (
            <Button
              ref={primaryButtonRef}
              size="small"
              variant="contained"
              disableElevation
              endIcon={intro ? <ArrowForwardIcon /> : undefined}
              onClick={onNext}
              sx={{ borderRadius: 999, px: 2, border: 1, ...actionSx }}
            >
              {intro ? t('start') : t('finish')}
            </Button>
          )}
        </Stack>
      </Paper>
    </ThemeProvider>
  )
}

/** Pastille boussole en verre sur le fond bleu : la marque du tour. */
const CompassBadge: FC<{ size: number }> = ({ size }) => (
  <Box
    aria-hidden
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color: WHITE,
      bgcolor: alpha(WHITE, 0.16),
      border: `1px solid ${alpha(WHITE, 0.28)}`,
    }}
  >
    <ProductTourIcon sx={{ fontSize: Math.round(size * 0.55) }} />
  </Box>
)

const ProgressDots: FC<{
  stepIndex: number
  total: number
  onSelect?: (position: number) => void
}> = ({ stepIndex, total, onSelect }) => {
  const { t } = useTranslate('productTour')
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      sx={{ mt: 2, ml: -0.25, gap: 0.25 }}
      aria-label={t('stepCounter', { current: stepIndex + 1, total })}
    >
      {Array.from({ length: total }, (_, index) => (
        <ButtonBase
          key={index}
          aria-label={t('goToStep', { current: index + 1, total })}
          aria-current={index === stepIndex ? 'step' : undefined}
          onClick={() => onSelect?.(index)}
          /* Zone de clic plus grande que la pastille. */
          sx={{ p: 0.25, borderRadius: 3 }}
        >
          <Box
            sx={(theme) => ({
              width: index === stepIndex ? 16 : 6,
              height: 6,
              borderRadius: 3,
              bgcolor:
                index === stepIndex
                  ? cyan.light
                  : alpha(WHITE, index < stepIndex ? 0.55 : 0.25),
              transition: theme.transitions.create([
                'width',
                'background-color',
              ]),
            })}
          />
        </ButtonBase>
      ))}
    </Stack>
  )
}

const RoundButton: FC<{
  label: string
  onClick: () => void
  filled?: boolean
  buttonRef?: Ref<HTMLButtonElement>
  children: ReactNode
}> = ({ label, onClick, filled = false, buttonRef, children }) => (
  <IconButton
    ref={buttonRef}
    size="small"
    aria-label={label}
    onClick={onClick}
    sx={{
      width: 30,
      height: 30,
      border: 1,
      ...(filled
        ? actionSx
        : {
            color: WHITE,
            borderColor: alpha(WHITE, 0.35),
            '&:hover': { bgcolor: alpha(WHITE, 0.1) },
          }),
    }}
  >
    {children}
  </IconButton>
)
